<script setup lang="ts">
    import { computed } from 'vue';
    import { getLibraryLink, getQuantityLink } from '@/shared/helpers/routing';
    import { SchemaAttribute, SchemaClass, SchemaDTO } from '@/shared/helpers/api/types/schemaModel.ts';

    type Relation = {
        description?: string;
        name: string;
        primeInterfaceName: string;
        Cardinality: string;
        primeRole: string;
        secondaryRole: string;
        secondaryInterfaceName: string;
    };

    const props = defineProps<{
        schema: SchemaDTO;
    }>();

    const relevantClasses = computed(() => {
        return props.schema.classes.filter(c => c.schema === props.schema.name);
    });

    const selectedClass = defineModel<SchemaClass | undefined>();

    const selectedClassAttributes = computed<SchemaAttribute[] | undefined>(() => {
        return selectedClass.value?.attributes;
    });

    const selectedClassRelations = computed<Relation[]>(() => {
        const relevantRelations: Relation[] = [];

        props.schema.relations.forEach(schemaRelation => {
            if (selectedClass.value?.interfaces.includes(schemaRelation.left.interfaceName)) {
                relevantRelations.push({
                    description: schemaRelation.description,
                    name: schemaRelation.name,
                    primeInterfaceName: schemaRelation.left.interfaceName,
                    Cardinality: `${schemaRelation.left.cardinality ?? 'null'}:${schemaRelation.right.cardinality ?? 'null'}`,
                    primeRole: schemaRelation.left.name,
                    secondaryRole: schemaRelation.right.name,
                    secondaryInterfaceName: schemaRelation.right.interfaceName
                });
            }
            if (selectedClass.value?.interfaces.includes(schemaRelation.right.interfaceName)) {
                relevantRelations.push({
                    description: schemaRelation.description,
                    name: schemaRelation.name,
                    primeInterfaceName: schemaRelation.right.interfaceName,
                    Cardinality: `${schemaRelation.right.cardinality ?? 'null'}:${schemaRelation.left.cardinality ?? 'null'}`,
                    primeRole: schemaRelation.right.name,
                    secondaryRole: schemaRelation.left.name,
                    secondaryInterfaceName: schemaRelation.left.interfaceName
                });
            }
        });
        return relevantRelations;
    });

    function selectClass(c: SchemaClass): void {
        if (selectedClass.value && selectedClass.value.name === c.name) {
            return;
        }
        selectedClass.value = c;
    }

    function isActive(c: SchemaClass): boolean | undefined {
        return selectedClass.value && selectedClass.value.name === c.name;
    }

    function datatype(attribute: SchemaAttribute): string {
        switch (attribute.dataType) {
            case 'enum':
                if (attribute.enumLibrary)
                    return 'enum:';
                return 'enum';
            case 'quantity':
                return 'quantity:';
            case 'integer':
                return `integer (${attribute.minValue}-${attribute.maxValue})`;
            case 'string':
                return `string (${attribute.maxValue})`;
            default:
                return attribute.dataType;
        }
    }

    function required(x: boolean): string {
        return x ? 'yes' : '';
    }

    function defaultUoM(attribute: SchemaAttribute): string {
        if (attribute.defaultUnitOfMeasure) {
            const suffixLength = attribute.quantity.length + 1;
            // example defaultUnitOfMeasure: m|length
            return attribute.defaultUnitOfMeasure.substring(0, attribute.defaultUnitOfMeasure.length - suffixLength);
        }
        return '';
    }
</script>

<template>
    <div class="columns">
        <div class="column is-2">
            <b-menu>
                <b-menu-list label="Select class">
                    <b-menu-item
                        v-for="c in relevantClasses"
                        :id="c.name"
                        :key="c.name"
                        :model-value="isActive(c)"
                        icon="format-list-bulleted"
                        :label="c.name"
                        @click="selectClass(c)" />
                </b-menu-list>
            </b-menu>
        </div>
        <div class="column">
            <div v-if="selectedClass">
                <h2 class="title">
                    {{ selectedClass.name }}
                </h2>
                <p>{{ selectedClass.comments }}</p>
                <b-table
                    :data="selectedClassAttributes"
                    :striped="true"
                    :narrowed="true"
                    :hoverable="true"
                    class="classTable">
                    <b-table-column
                        v-slot="slotProps"
                        sortable
                        field="interfaceName"
                        label="Interface">
                        {{ slotProps.row.interfaceName }}
                    </b-table-column>
                    <b-table-column
                        v-slot="slotProps"
                        sortable
                        field="name"
                        label="Attribute name">
                        {{ slotProps.row.name }}
                    </b-table-column>
                    <b-table-column
                        v-slot="slotProps"
                        sortable
                        field="description"
                        label="Suggested display as">
                        {{ slotProps.row.description }}
                    </b-table-column>
                    <b-table-column
                        v-slot="slotProps"
                        sortable
                        field="dataType"
                        label="Type">
                        {{ datatype(slotProps.row) }}
                        <router-link
                            v-if="slotProps.row.enumLibrary || slotProps.row.quantity"
                            :to="slotProps.row.enumLibrary ?
                                getLibraryLink(slotProps.row.enumLibrary) : getQuantityLink(slotProps.row.quantity)">
                            {{ slotProps.row.enumLibrary ? slotProps.row.enumLibrary : slotProps.row.quantity }}
                        </router-link>
                    </b-table-column>
                    <b-table-column
                        v-slot="slotProps"
                        sortable
                        field="defaultUnitOfMeasure"
                        label="Default UoM">
                        <b-tooltip label="Default Unit of Measure">
                            {{ defaultUoM(slotProps.row) }}
                        </b-tooltip>
                    </b-table-column>
                    <b-table-column
                        v-slot="slotProps"
                        sortable
                        field="required"
                        label="Required">
                        {{ required(slotProps.row.required) }}
                    </b-table-column>
                    <b-table-column
                        v-slot="slotProps"
                        sortable
                        field="comments"
                        label="Comments">
                        {{ slotProps.row.comments }}
                    </b-table-column>
                </b-table>
            </div>
            <div v-if="selectedClassRelations.length > 0">
                <h3>{{ selectedClass?.name }} may participate in following relations:</h3>
                <b-table
                    :data="selectedClassRelations"
                    :narrowed="true"
                    class="class-table">
                    <b-table-column
                        v-slot="slotProps"
                        sortable
                        field="name"
                        label="Relation Name">
                        {{ slotProps.row.name }}
                    </b-table-column>
                    <b-table-column
                        v-slot="slotProps"
                        sortable
                        field="description"
                        label="Description">
                        {{ slotProps.row.description }}
                    </b-table-column>
                    <b-table-column
                        v-slot="slotProps"
                        sortable
                        field="primeInterfaceName"
                        :label="selectedClass?.name + ' interface'">
                        {{ slotProps.row.primeInterfaceName }}
                    </b-table-column>
                    <b-table-column
                        v-slot="slotProps"
                        sortable
                        field="primeRole"
                        label="Role">
                        {{ slotProps.row.primeRole }}
                    </b-table-column>
                    <b-table-column
                        v-slot="slotProps"
                        sortable
                        field="Cardinality"
                        label="Cardinality">
                        {{ slotProps.row.Cardinality }}
                    </b-table-column>
                    <b-table-column
                        v-slot="slotProps"
                        sortable
                        field="secondaryRole"
                        label="Other side role">
                        {{ slotProps.row.secondaryRole }}
                    </b-table-column>
                    <b-table-column
                        v-slot="slotProps"
                        sortable
                        field="secondaryInterfaceName"
                        label="Other side interface">
                        {{ slotProps.row.secondaryInterfaceName }}
                    </b-table-column>
                </b-table>
            </div>
            <div v-else-if="selectedClass">
                No relations found for {{ selectedClass.name }}
            </div>
        </div>
    </div>
</template>

<style scoped>

</style>
